import { useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'
import { TabProps, RechartsClickData } from './types'
import { useFormatters, usePreferences } from '../../contexts/PreferencesContext'
import { ChartWrapper } from './ChartWrapper'
import { useDrilldownAction, OTIFDrilldownModal, OTDDrilldownModal } from '../drilldown'
import { useQualityMeasureLabel } from '../../lib/qualityLabels'
import { groupDetailDrillFilters } from './dashboardFilterUtils'
import { firstTarget, judgeRow, judgeClass } from './kpiTargets'
import { formatKpiPercent } from '../../lib/kpiPercent'
import { showsOtd, showsOtif } from './deliveryMetricMode'

export function GroupDetailKPITab({ data, currency: _currency, filters, orderType }: TabProps) {
  const { formatCurrency: _formatCurrency, formatNumber } = useFormatters()
  // OTIF/KPI percentage decimals from Admin Setup -> Regional -> Decimal
  // Precision. This tab shows its percentages in a table and a chart
  // tooltip rather than rings, but they are the same KPI figures and would
  // otherwise disagree with every other tab.
  const { preferences } = usePreferences()
  const kpiDecimals = preferences.kpi_percent_decimals ?? 0
  const { openDrilldown } = useDrilldownAction()
  const qualityLabel = useQualityMeasureLabel()

  // Delivery metric mode: conditionally show OTIF and/or OTD sections
  const showOTIF = showsOtif(data.delivery_metric_mode)  // CR-8697: helper knows 'none'
  const showOTD = showsOtd(data.delivery_metric_mode)

  // OTIF/OTD Modal state
  const [isOTIFModalOpen, setIsOTIFModalOpen] = useState(false)
  const [otifModalContext, setOtifModalContext] = useState<string | undefined>(undefined)
  const [isOTDModalOpen, setIsOTDModalOpen] = useState(false)
  const [otdModalContext, setOtdModalContext] = useState<string | undefined>(undefined)

  // Drill filters in the drill API's key names (R-158), with the W3-T5
  // capped window and arrears term the charts above use. A clicked site
  // bar narrows the OTIF and OTD drills to that site (see handleChartClick).
  const drilldownFilters = useMemo(
    () => groupDetailDrillFilters(filters, null, undefined, orderType), [filters, orderType])
  // The site the OTIF / OTD drill is scoped to — set by the bars AND the table
  // row, derived at render so a panel change (mode, dates) is never stale.
  const [clickedCustomerId, setClickedCustomerId] = useState<number | null>(null)
  const siteDrillFilters = useMemo(
    () => groupDetailDrillFilters(filters, clickedCustomerId, undefined, orderType),
    [filters, clickedCustomerId, orderType])

  // Handle chart click for drill-down
  const handleChartClick = (chartType: string, clickData?: RechartsClickData) => {
    // Recharts 3 reports a chart click as activeLabel / activeIndex — the
    // activePayload the older handler read is no longer sent, which is why
    // every click here used to fall through to "All Suppliers". The label is
    // the site name (XAxis dataKey), resolved back to its row for the id.
    const payload = clickData?.activePayload?.[0]?.payload
    const supplier = (payload?.['supplier'] as string | undefined) ?? clickData?.activeLabel
    const clickedCustomerId = (payload?.['customerId'] as number | undefined)
      ?? supplierOtifData.find((row) => row.supplier === supplier)?.customerId
      ?? supplierOtdData.find((row) => row.supplier === supplier)?.customerId

    switch (chartType) {
      case 'otif':
        setClickedCustomerId(clickedCustomerId ?? null)
        setOtifModalContext(supplier || 'All Suppliers')
        setIsOTIFModalOpen(true)
        break
      case 'otd':
        // CR-9069: the clicked site scopes the OTD drill too; it opened on the
        // whole tab's scope, so every bar listed the same site(s).
        setClickedCustomerId(clickedCustomerId ?? null)
        setOtdModalContext(supplier || 'All Suppliers')
        setIsOTDModalOpen(true)
        break
      case 'ppm':
        // CR-7714: a clicked site scopes the drill to that site, as OTIF does,
        // so the title never names one site over the whole group's rejects.
        // Only the OTIF rows carry the site id (none in OTD-only mode, none
        // for a site with no OTIF row): without it the drill is the whole
        // group, titled "All Suppliers".
        if (supplier && clickedCustomerId != null) {
          openDrilldown('ppm', groupDetailDrillFilters(filters, clickedCustomerId, undefined, orderType), supplier)
        } else {
          openDrilldown('ppm', drilldownFilters, 'All Suppliers')
        }
        break
      default:
        openDrilldown('spend', drilldownFilters)
    }
  }

  // CR-4211: Use real data from API instead of Math.random()
  // Q46 / CR-7714 FE-20: the configured targets only. No target means no
  // colour and no target line, never an invented 95 / 1500 / 90.
  const otifTarget = firstTarget(data.targets?.otif, data.quality?.otifTarget)
  const ppmTarget = firstTarget(data.targets?.ppm, data.quality?.ppmTarget)
  const otdTarget = firstTarget(data.targets?.otd, data.delivery?.otdTarget)

  // CR-6543: join the three backend arrays by NAME, not array index — the
  // backend does not guarantee ordering, so an index zip could put supplier
  // A's OTIF beside supplier B's OTD. Counts come from the backend payload
  // (total_lines / otif_lines / arrears_lines) instead of being
  // back-derived from the rounded percentage.
  const supplierOtifData = data.siteKPI?.otifBySite?.map((site) => {
    const deliveryEntry = data.siteKPI?.deliveryBySite?.find(
      (d) => d.site === site.site,
    )
    const linesDelivered = site.total_lines
      ?? (deliveryEntry
        ? (deliveryEntry.total ?? deliveryEntry.onTime + deliveryEntry.early + deliveryEntry.late)
        : site.orders || 0)
    // METRIC-DEFINITIONS M3 (wave 3a): total_lines is the CLOSED judged count
    // (C), disjoint from arrears — failed is simply C minus passed.
    const linesFailed = site.total_lines !== undefined && site.otif_lines !== undefined
      ? Math.max(0, site.total_lines - site.otif_lines)
      : 0
    return {
      supplier: site.site,
      customerId: site.customer_id,
      grain: site.grain,
      otif: site.otif,
      target: site.target ?? otifTarget,
      linesDelivered,
      linesFailed,
    }
  }) || []

  const supplierOtdData = data.siteKPI?.deliveryBySite?.map((site) => ({
    supplier: site.site,
    customerId: site.customer_id,
    onTime: site.onTime,
    early: site.early,
    late: site.late,
    // CR-6543: backend total (every GRN — the ring's denominator); the
    // bucket sum remains only as a legacy-payload fallback.
    total: site.total ?? site.onTime + site.early + site.late,
  })) || []

  const supplierPpmData = data.siteKPI?.ppmBySite?.map((site) => ({
    // CR-7714: ppm_by_company is keyed by `company` (no `site` alias, as on
    // the Site KPI chart, CR-5868); keyed on `site` no row ever matched, so
    // every bar was unlabelled and the summary table read PPM 0.
    supplier: site.company,
    ppm: site.ppm,
    target: site.target ?? ppmTarget,
    // CR-8698: the backend's own quantities, shown as whole parts. `?? 0`
    // covers a response cached before they were sent (5 minutes).
    rejected: Math.round(site.rejected ?? 0),
    received: Math.round(site.received ?? 0),
  })) || []
  type SupplierPpmRow = (typeof supplierPpmData)[number]

  return (
    <div className="space-y-4">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          Group Detail KPI provides supplier-level breakdown of performance metrics.
          Click on any supplier to drill down into their specific performance data.
        </p>
      </div>

      {/* Supplier OTIF Breakdown - CR-3835: Fixed site name truncation */}
      {showOTIF && (
      <ChartWrapper
        title="OTIF by Supplier"
        data={supplierOtifData}
        kpiPercentDecimals={kpiDecimals}
        exportFilename="otif-by-supplier"
        legend={[
          { color: '#10b981', label: 'OTIF %' },
          { color: '#ef4444', label: 'Target' },
        ]}
      >
        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={supplierOtifData}
            onClick={(e) => handleChartClick('otif', e)}
            style={{ cursor: 'pointer' }}
            margin={{ bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="supplier"
              tick={{ fontSize: 9 }}
              angle={-45}
              textAnchor="end"
              height={120}
              interval={0}
            />
            <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
            <Tooltip
              cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
              formatter={(value: number, name: string) => [
                name === 'otif' ? formatKpiPercent(value, kpiDecimals) : value,
                name === 'otif' ? 'OTIF' : name
              ]}
            />
            <Bar dataKey="otif" fill="#10b981" name="OTIF" />
            {otifTarget !== null && <ReferenceLine y={otifTarget} stroke="#ef4444" strokeDasharray="5 5" label={{ value: `Target ${otifTarget}%`, position: 'right' }} />}
          </BarChart>
        </ResponsiveContainer>
      </ChartWrapper>
      )}

      {/* Supplier OTD Breakdown - CR-3835: Fixed site name truncation */}
      {showOTD && (
      <ChartWrapper
        title="Delivery Performance by Supplier"
        data={supplierOtdData}
        exportFilename="delivery-by-supplier"
        legend={[
          { color: '#10b981', label: 'On Time' },
          { color: '#fbbf24', label: 'Early' },
          { color: '#ef4444', label: 'Late' },
        ]}
      >
        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={supplierOtdData}
            onClick={(e) => handleChartClick('otd', e)}
            style={{ cursor: 'pointer' }}
            margin={{ bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="supplier"
              tick={{ fontSize: 9 }}
              angle={-45}
              textAnchor="end"
              height={120}
              interval={0}
            />
            <YAxis />
            <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
            <Bar dataKey="onTime" stackId="delivery" fill="#10b981" name="On Time" />
            <Bar dataKey="early" stackId="delivery" fill="#fbbf24" name="Early" />
            <Bar dataKey="late" stackId="delivery" fill="#ef4444" name="Late" />
          </BarChart>
        </ResponsiveContainer>
      </ChartWrapper>
      )}

      {/* Supplier PPM Breakdown - CR-3835: Fixed site name truncation */}
      <ChartWrapper
        title={`${qualityLabel} by Supplier`}
        data={supplierPpmData}
        exportFilename="ppm-by-supplier"
        columns={[
          { key: 'supplier', header: 'Site' },
          { key: 'ppm', header: qualityLabel },
          { key: 'rejected', header: 'Parts Rejected' },
          { key: 'received', header: 'Parts Delivered' },
        ]}
        legend={[
          { color: '#ef4444', label: qualityLabel },
          { color: '#10b981', label: 'Target' },
        ]}
      >
        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={supplierPpmData}
            onClick={(e) => handleChartClick('ppm', e)}
            style={{ cursor: 'pointer' }}
            margin={{ bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="supplier"
              tick={{ fontSize: 9 }}
              angle={-45}
              textAnchor="end"
              height={120}
              interval={0}
            />
            <YAxis />
            <Tooltip
              cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
              // CR-8698: Recharts drops null values from the payload by
              // default, so a site with no PPM would get no tooltip at all.
              filterNull={false}
              content={({ active, payload, label }) => {
                if (!active || !payload || payload.length === 0) return null
                const row = payload[0].payload as SupplierPpmRow
                return (
                  <div className="bg-white border rounded-lg shadow-lg p-3 text-sm">
                    <p className="font-medium mb-1">{label}</p>
                    <p className="text-gray-600">{qualityLabel}: {row.ppm === null ? 'N/A' : formatNumber(row.ppm)}</p>
                    <p className="text-gray-600">Parts rejected: {formatNumber(row.rejected)}</p>
                    <p className="text-gray-600">Parts delivered: {formatNumber(row.received)}</p>
                  </div>
                )
              }}
            />
            <Bar dataKey="ppm" fill="#ef4444" name={qualityLabel} />
            {ppmTarget !== null && <ReferenceLine y={ppmTarget} stroke="#10b981" strokeDasharray="5 5" label={{ value: `Target ${ppmTarget}`, position: 'right' }} />}
          </BarChart>
        </ResponsiveContainer>
      </ChartWrapper>

      {/* Supplier Summary Table */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Supplier Performance Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-medium">Supplier</th>
                  {showOTIF && <th className="text-right py-2 px-3 font-medium">OTIF %</th>}
                  {showOTD && <th className="text-right py-2 px-3 font-medium">OTD %</th>}
                  <th className="text-right py-2 px-3 font-medium">{qualityLabel}</th>
                  <th className="text-right py-2 px-3 font-medium">Lines Delivered</th>
                  <th className="text-right py-2 px-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {supplierOtifData.slice(0, 10).map((supplier, idx) => {
                  // CR-6543: match rows by supplier name — index zipping
                  // could pair one supplier's OTIF with another's OTD/PPM.
                  const otdEntry = supplierOtdData.find((d) => d.supplier === supplier.supplier)
                  const ppmEntry = supplierPpmData.find((p) => p.supplier === supplier.supplier)
                  // OTD = passing / all GRNs, mirroring the headline ring
                  // (early counts against, denominator includes unstatused).
                  // CR-7714 FE-7: no GRNs → OTD N/A, not a red 0%; no PPM
                  // entry → N/A, not a green 0. Unknowns are neither good nor bad.
                  const otd = otdEntry && otdEntry.total > 0 ? (otdEntry.onTime / otdEntry.total * 100) : null
                  const ppm = ppmEntry?.ppm ?? null
                  const verdict = judgeRow([
                    { value: supplier.otif, target: otifTarget, higherIsBetter: true, shown: showOTIF },
                    { value: otd, target: otdTarget, higherIsBetter: true, shown: showOTD },
                    { value: ppm, target: ppmTarget, higherIsBetter: false, shown: true },
                  ])

                  return (
                    <tr
                      key={idx}
                      className="border-b hover:bg-muted/50 cursor-pointer"
                      onClick={() => {
                        setClickedCustomerId(supplier.customerId ?? null)
                        setOtifModalContext(supplier.supplier)
                        setIsOTIFModalOpen(true)
                      }}
                    >
                      <td className="py-2 px-3">
                        {supplier.supplier}
                        {supplier.grain === 'schedules' && (
                          <span className="ml-2 text-xs text-muted-foreground" title="Judged on its schedule lines; Lines Delivered counts schedule slots">schedule lines</span>
                        )}
                      </td>
                      {showOTIF && (
                      <td className={`text-right py-2 px-3 ${judgeClass(supplier.otif, otifTarget, true)}`}>
                        {formatKpiPercent(supplier.otif, kpiDecimals)}
                      </td>
                      )}
                      {showOTD && (
                      <td className={`text-right py-2 px-3 ${judgeClass(otd, otdTarget, true)}`}>
                        {formatKpiPercent(otd, kpiDecimals)}
                      </td>
                      )}
                      <td className={`text-right py-2 px-3 ${judgeClass(ppm, ppmTarget, false)}`}>
                        {ppm === null ? 'N/A' : formatNumber(ppm)}
                      </td>
                      <td className="text-right py-2 px-3">{formatNumber(supplier.linesDelivered)}</td>
                      <td className="text-right py-2 px-3">
                        <span
                          className={`px-2 py-1 rounded text-xs ${verdict === 'good' ? 'bg-green-100 text-green-800' : verdict === 'attention' ? 'bg-red-100 text-red-800' : 'bg-muted text-muted-foreground'}`}
                          title={verdict === 'na' ? 'Not enough data to judge' : undefined}
                        >
                          {verdict === 'good' ? 'Good' : verdict === 'attention' ? 'Attention' : 'N/A'}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* OTIF Specialized Drill-Down Modal */}
      <OTIFDrilldownModal
        isOpen={isOTIFModalOpen}
        onClose={() => setIsOTIFModalOpen(false)}
        filters={siteDrillFilters}
        contextLabel={otifModalContext}
      />

      {/* OTD Specialized Drill-Down Modal */}
      <OTDDrilldownModal
        isOpen={isOTDModalOpen}
        onClose={() => setIsOTDModalOpen(false)}
        filters={siteDrillFilters}
        contextLabel={otdModalContext}
      />
    </div>
  )
}
